<template>
  <div ref="el" class="relative aspect-[3/2] w-full overflow-hidden bg-muted">
    <!-- Картинка маршрута 3:2: кусок нашей карты с линией (routePreviewRenderer).
         Пока снимка нет - фон и та же линия в SVG; без линии - только фон. -->
    <img v-if="src" :src="src" alt="" class="absolute inset-0 h-full w-full object-cover" draggable="false" />
    <svg
      v-else-if="svg"
      class="absolute inset-0 h-full w-full"
      :viewBox="`0 0 ${VB_W} ${VB_H}`"
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
    >
      <polyline :points="svg.points" fill="none" stroke="#ffffff" stroke-width="7" stroke-linejoin="round" stroke-linecap="round" />
      <polyline :points="svg.points" fill="none" :stroke="color" stroke-width="4" stroke-linejoin="round" stroke-linecap="round" />
      <circle :cx="svg.start[0]" :cy="svg.start[1]" r="5" fill="#ffffff" :stroke="color" stroke-width="2.5" />
      <circle :cx="svg.end[0]" :cy="svg.end[1]" r="5" :fill="color" stroke="#ffffff" stroke-width="2.5" />
    </svg>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { renderRoutePreview } from "../../map/routePreviewRenderer";
import { useTheme } from "../../composables/useTheme";
import { lineKey } from "../../utils/lineKey";

const props = defineProps<{ routeId: number; line: [number, number][] | null; color: string }>();

const VB_W = 300;
const VB_H = 200;
const PAD = 0.12;

const el = ref<HTMLElement | null>(null);
const src = ref("");
const visible = ref(false);
const { isDark } = useTheme();

const hasLine = computed(() => !!props.line && props.line.length >= 2);

// Линия в координатах viewBox: долгота сжата на cos(широты), чтобы форма
// совпадала с картой, масштаб - вписать с полями, по центру.
const svg = computed(() => {
  if (!hasLine.value) return null;
  const line = props.line!;
  const k = Math.cos((line[0][1] * Math.PI) / 180);
  const pts = line.map(([lon, lat]) => [lon * k, -lat] as const);
  const xs = pts.map((p) => p[0]);
  const ys = pts.map((p) => p[1]);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  const w = Math.max(...xs) - minX || 1e-9;
  const h = Math.max(...ys) - minY || 1e-9;
  const scale = Math.min((VB_W * (1 - 2 * PAD)) / w, (VB_H * (1 - 2 * PAD)) / h);
  const ox = (VB_W - w * scale) / 2;
  const oy = (VB_H - h * scale) / 2;
  const xy = pts.map(([x, y]) => [+(ox + (x - minX) * scale).toFixed(1), +(oy + (y - minY) * scale).toFixed(1)] as const);
  return { points: xy.map((p) => p.join(",")).join(" "), start: xy[0], end: xy[xy.length - 1] };
});

// Размер снимка - по ширине карточки (шаг 20px, чтобы соседние карточки
// попадали в один ключ кеша); в тестовом DOM ширины нет - берём типичную.
const size = () => {
  const raw = el.value?.clientWidth || 360;
  const width = Math.min(640, Math.max(160, Math.round(raw / 20) * 20));
  return { width, height: Math.round((width * 2) / 3) };
};

// Содержимое линии, а не ссылка: обновление стора приносит новый, но
// равный массив - перезапуск дал бы мигание SVG-заглушки.
const key = computed(() => `${lineKey(props.line)}|${props.color}|${isDark.value}`);

let ticket = 0;
let pending: AbortController | null = null;
const cancelPending = () => {
  pending?.abort();
  pending = null;
  ticket++;
};

const render = async () => {
  cancelPending();
  if (!visible.value || !hasLine.value) {
    src.value = "";
    return;
  }
  const mine = ticket;
  const ctl = new AbortController();
  pending = ctl;
  const url = await renderRoutePreview(props.line!, { ...size(), dark: isDark.value, color: props.color, signal: ctl.signal });
  // Пока рисовалось, пропсы/тема могли смениться - устаревший снимок не ставим.
  if (mine !== ticket) return;
  pending = null;
  src.value = url;
  if (url) {
    observer?.disconnect();
    observer = null;
  }
};

watch(key, () => {
  src.value = "";
  void render();
});

let observer: IntersectionObserver | null = null;
onMounted(() => {
  if (typeof IntersectionObserver === "undefined" || !el.value) {
    visible.value = true;
    void render();
    return;
  }
  // Наблюдатель живёт до готового снимка: ушла карточка с экрана раньше -
  // её ещё не начатый рендер снимается из очереди, вернулась - запрос снова.
  observer = new IntersectionObserver(
    (entries) => {
      const inView = entries.some((e) => e.isIntersecting);
      if (inView && !visible.value) {
        visible.value = true;
        void render();
      } else if (!inView && visible.value && !src.value) {
        visible.value = false;
        cancelPending();
      }
    },
    { rootMargin: "200px" }
  );
  observer.observe(el.value);
});

onBeforeUnmount(() => {
  observer?.disconnect();
  observer = null;
  cancelPending();
});
</script>
